import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import {
  LMS_CREATOR_NOTIFICATION_EVENT_CODES,
  LMS_LEARNER_NOTIFICATION_EVENT_CODES,
  STOREFRONT_DEFAULT,
  resolveStorefront,
  type StorefrontConfig,
} from '@mlm/shared';
import { getTenantFeatures, patchTenantFeatures, type TenantFeatures } from '../lib/api';
import { hasRole } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';
import {
  EventTemplateEditor,
  MerchantRequirementsEditor,
  Toggle,
} from '../components/settings/shared-fields';

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/**
 * Learning Settings — every switch that governs courses, creators and learners.
 *
 * These controls used to be scattered: an 18-control card inside Settings →
 * System, the LMS notification templates inside that page's Notifications card,
 * and four toggles inside Storefront → Content. They are the same `features`
 * keys as before, moved (not reshaped), so nothing about a live tenant changes.
 *
 * The LMS MASTER SWITCH deliberately stays in Settings → System: this whole
 * section of the sidebar is hidden while the LMS is off, so the switch that
 * turns it on cannot live inside it.
 *
 * Two stores are edited here and they save SEPARATELY: the feature keys, and
 * the `storefront` block that carries the four course-page toggles. Sending one
 * PATCH for both would widen what a `tenant.storefront.write` holder can reach.
 */
export function LearningSettingsPage() {
  const qc = useQueryClient();
  const canWrite = hasRole('super_admin');
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  const [draft, setDraft] = useState<TenantFeatures | null>(null);
  const [storefront, setStorefront] = useState<StorefrontConfig | null>(null);
  useEffect(() => {
    if (q.data && !draft) {
      setDraft(q.data);
      setStorefront(clone(resolveStorefront(q.data.storefront ?? STOREFRONT_DEFAULT)));
    }
  }, [q.data, draft]);

  const save = useMutation({
    meta: { successMessage: 'Learning settings saved.' },
    mutationFn: async (next: { features: TenantFeatures; storefront: StorefrontConfig }) => {
      await patchTenantFeatures(next.features);
      await patchTenantFeatures({ storefront: next.storefront });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-features'] });
      qc.invalidateQueries({ queryKey: ['storefront-content'] });
    },
  });

  if (q.isLoading || !draft || !storefront) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  if (!draft.lms_enabled) {
    return (
      <div className="max-w-2xl space-y-3">
        <h1 className="text-2xl font-semibold">Learning Settings</h1>
        <p className="text-sm text-muted-foreground">
          The Learning Management System is switched off for this tenant. Turn it on under{' '}
          <Link to="/settings#learning" className="underline">
            Settings → System → Learning
          </Link>{' '}
          to configure it.
        </p>
      </div>
    );
  }

  // Both save buttons diff the SAME pair of drafts — the header one is a
  // convenience, not a second action.
  //
  // The storefront side of the diff compares RESOLVED against RESOLVED: the
  // stored blob may predate half the block's fields, and diffing it raw would
  // report every default the resolver filled in as a change the admin is about
  // to make.
  const SaveButton = () =>
    canWrite ? (
      <ConfirmSaveButton
        title="Save Learning Settings"
        current={{
          features: q.data,
          storefront: resolveStorefront(q.data?.storefront ?? STOREFRONT_DEFAULT),
        }}
        next={{ features: draft, storefront }}
        loading={save.isPending}
        onConfirm={() => save.mutateAsync({ features: draft, storefront })}
      />
    ) : (
      <p className="text-sm text-muted-foreground">Only super_admin can change these settings.</p>
    );

  return (
    <div className="flex gap-6">
      <aside className="sticky top-20 hidden h-fit w-52 shrink-0 lg:block">
        <nav className="space-y-1 text-sm text-muted-foreground">
          <a href="#learning-mode" className="block rounded px-2 py-1 hover:bg-muted">
            Learner experience
          </a>
          <a href="#content-review" className="block rounded px-2 py-1 hover:bg-muted">
            Content review
          </a>
          <a href="#subscriptions" className="block rounded px-2 py-1 hover:bg-muted">
            Course subscriptions
          </a>
          <a href="#learners" className="block rounded px-2 py-1 hover:bg-muted">
            Learners
          </a>
          <a href="#creators" className="block rounded px-2 py-1 hover:bg-muted">
            Creators
          </a>
          <a href="#media" className="block rounded px-2 py-1 hover:bg-muted">
            Video and storage
          </a>
          <a href="#storefront" className="block rounded px-2 py-1 hover:bg-muted">
            Course pages
          </a>
          <a href="#notifications" className="block rounded px-2 py-1 hover:bg-muted">
            Notifications
          </a>
        </nav>
      </aside>

      <div className="min-w-0 max-w-3xl flex-1 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Learning Settings</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Courses, creators and the learner experience. The master switch that turns the LMS on
              and off stays in{' '}
              <Link to="/settings#learning" className="underline">
                Settings → System
              </Link>
              .
            </p>
          </div>
          <SaveButton />
        </div>

        <Card id="learning-mode" className="scroll-mt-20">
          <CardHeader>
            <CardTitle>Learner experience</CardTitle>
            <p className="text-sm text-muted-foreground">
              How the LMS presents itself to members: which dashboard they land on, and whether
              courses can be rung up at the counter.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Member dashboard style</label>
              <Select
                value={draft.member_dashboard_mode ?? 'mlm'}
                disabled={!canWrite}
                onValueChange={(v) =>
                  setDraft({ ...draft, member_dashboard_mode: v as 'mlm' | 'learning' })
                }
              >
                <SelectTrigger className="h-8 w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mlm">MLM — passbook (wallet, standing, PV, slots)</SelectItem>
                  <SelectItem value="learning">Learning — classroom (courses first)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                "Learning" swaps the member dashboard to the classroom design (Jump back in, catalog
                strip, announcements), relabels the nav (Store → Catalog, Training → My Courses,
                Earnings → Rewards) and hides the MLM-only pages (Genealogy, Vault, Redeem, Leads,
                Live Links). Everything else — orders, cash-out, invites — stays. Default MLM;
                existing tenants are unchanged.
              </p>
            </div>
            <Toggle
              label="Show the Invite-a-friend card (learning mode)"
              description="When on, the classroom dashboard shows the member's referral link with joined/rewards stats. Turn off for a pure course tenant with no re-selling pitch — the Announcements card then spans full width. Default on."
              value={draft.show_team_earnings ?? true}
              disabled={!canWrite || (draft.member_dashboard_mode ?? 'mlm') !== 'learning'}
              onChange={(v) => setDraft({ ...draft, show_team_earnings: v })}
            />
            <div className="space-y-1">
              <label className="text-sm font-medium">Selling courses at the POS</label>
              <Select
                value={draft.pos_course_sale_mode ?? 'warn'}
                disabled={!canWrite}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    pos_course_sale_mode: v as 'allow' | 'warn' | 'block',
                  })
                }
              >
                <SelectTrigger className="h-8 w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warn">Warn — show the cashier who gets access</SelectItem>
                  <SelectItem value="allow">Allow — no special handling</SelectItem>
                  <SelectItem value="block">Block — courses cannot be sold at the till</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                A till sale always books to the member the cashier selected, so a course rung on the
                wrong account (a shared walk-in login, say) grants access to that account and not to
                the buyer. "Warn" (default) names the receiving member on the POS screen and again
                in the review dialog. "Block" refuses course products at the till entirely. Either
                way a mis-rung sale is fixed on Content → Course enrollments.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card id="content-review" className="scroll-mt-20">
          <CardHeader>
            <CardTitle>Content review</CardTitle>
            <p className="text-sm text-muted-foreground">
              What happens when a creator changes a course after it was approved, and whether
              members can report content.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                When a creator changes course content after approval
              </label>
              <Select
                value={draft.lms_content_rereview ?? 'hold'}
                disabled={!canWrite}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    lms_content_rereview: v as 'flag' | 'delist' | 'hold',
                  })
                }
              >
                <SelectTrigger className="h-8 w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hold">Hold — students keep the approved version</SelectItem>
                  <SelectItem value="flag">Flag — publish the change, queue for review</SelectItem>
                  <SelectItem value="delist">Delist — pause the listing until reviewed</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Swapping a lesson's video or article after an admin approved the listing used to be
                invisible — only a name, description or price edit re-opened review. Now the change
                is recorded per lesson and the course appears under Creators → Courses for content
                review. "Hold" (default) keeps the course selling and keeps your students on the
                video, link or article an admin last approved, while the creator's new one waits in
                the queue and goes live only when you approve it (Reject throws it away). It covers
                a lesson's playable content only — adding or deleting lessons, titles, ordering,
                drip dates and tests still take effect immediately and simply queue for review.
                "Flag" is the older behaviour: the course keeps selling AND the change reaches
                everyone who already bought it while it waits. "Delist" pauses the listing the way a
                price edit does, but existing students still see the change straight away.
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Which changes go back for review</label>
              <Select
                value={draft.lms_content_rereview_scope ?? 'all'}
                disabled={!canWrite}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    lms_content_rereview_scope: v as 'content' | 'all',
                  })
                }
              >
                <SelectTrigger className="h-8 w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Every edit — including titles and prices</SelectItem>
                  <SelectItem value="content">Content only — videos, articles, lessons</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                "Every edit" (default) also sends a renamed course, an edited description, a new
                price, a re-timed drip, a reordered chapter or an edited test back through review,
                and the Review dialog shows exactly what changed with a before/after compare.
                "Content only" reviews just the playable content. Attachments and captions are
                recorded but never gate, under either setting. The tenant's own house courses are
                never reviewed.
              </p>
            </div>
            <Toggle
              label="Member abuse reporting"
              value={draft.lms_abuse_reporting_enabled !== false}
              disabled={!canWrite}
              description="Adds a Report action to courses, reviews, Q&A and announcements, and routes what members send to Messages & Feedback → Moderation. Turn it off only for a closed, house-only catalog."
              onChange={(v) => setDraft({ ...draft, lms_abuse_reporting_enabled: v })}
            />
          </CardContent>
        </Card>

        <Card id="subscriptions" className="scroll-mt-20">
          <CardHeader>
            <CardTitle>Course subscriptions</CardTitle>
            <p className="text-sm text-muted-foreground">
              Who sells access to a subscribers-only course, and who is allowed to buy it. The
              tenant-wide member subscription (Special Links) is a separate product — it lives in
              Settings → System.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                Who sells access to a subscribers-only course
              </label>
              <Select
                value={draft.lms_subscription_model ?? 'tenant'}
                disabled={!canWrite}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    lms_subscription_model: v as 'tenant' | 'creator',
                  })
                }
              >
                <SelectTrigger className="h-8 w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant">The tenant — one plan unlocks everything</SelectItem>
                  <SelectItem value="creator">
                    Each creator — their own plan per instructor
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                "The tenant" (default) is the existing single monthly wallet subscription: one plan
                unlocks every subscribers-only course in the catalog. "Each creator" moves the sale
                to the instructor — they set a monthly, 6-month and 12-month price, and their plan
                unlocks only their own courses. A subscribers-only course carries no price of its
                own under either model. Switching re-gates courses you are already selling, so
                change it deliberately.
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Who may buy a subscription</label>
              <Select
                value={draft.lms_subscription_eligibility ?? 'activated_slot'}
                disabled={!canWrite}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    lms_subscription_eligibility: v as 'activated_slot' | 'any_member',
                  })
                }
              >
                <SelectTrigger className="h-8 w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activated_slot">Members with an activated slot</SelectItem>
                  <SelectItem value="any_member">Any signed-up member</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                "Activated slot" (default) matches what the wallet subscription always required.
                "Any signed-up member" lets someone who joined only to study buy a course
                subscription without activating a slot first — the right setting for a tenant
                selling courses to the public. Existing subscribers are never affected either way.
              </p>
            </div>
            <Toggle
              label="Let members renew automatically"
              value={draft.lms_subscription_auto_renew_enabled !== false}
              disabled={!canWrite || draft.lms_subscription_model !== 'creator'}
              description="Each creator's plans only. A member who pays from a slot wallet can tick auto-renew: about a day before their access ends, a new period is bought from that wallet at the price they agreed to. A failed charge is retried daily for a week, and the member is told. Off: nothing is charged, and members get a reminder three days before their access ends with a Renew now button."
              onChange={(v) => setDraft({ ...draft, lms_subscription_auto_renew_enabled: v })}
            />
            <Toggle
              label="Card payments renew automatically (Stripe)"
              value={draft.lms_subscription_card_recurring === true}
              disabled={!canWrite || draft.lms_subscription_model !== 'creator'}
              description="Your own plans only, paid with a Stripe card method. Checkout opens a Stripe subscription instead of buying one period: Stripe charges the card each month, 6 months or year at the price the member signed up at, and every payment it collects adds the next period here. Members update their card or cancel from My Courses (Manage billing). A failed charge is retried by Stripe and the member is told. Manual Pay, Dragonpay and iPay88 always buy one period. Turning this off affects new checkouts only; open subscriptions keep billing until they are cancelled."
              onChange={(v) => setDraft({ ...draft, lms_subscription_card_recurring: v })}
            />
          </CardContent>
        </Card>

        <Card id="learners" className="scroll-mt-20">
          <CardHeader>
            <CardTitle>Learners</CardTitle>
            <p className="text-sm text-muted-foreground">
              Playback and completion rules, and the nudge that brings an idle learner back.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <Toggle
              label="Stop learners skipping ahead"
              value={draft.lms_seek_restriction === true}
              disabled={!canWrite}
              description="A learner can only scrub within the part of a video they have already watched. Reaching the end of a lesson once unlocks that lesson for good. Existing progress is honoured, so nobody is sent back to the start when you turn this on. It discourages skipping; it is not copy protection."
              onChange={(v) => setDraft({ ...draft, lms_seek_restriction: v })}
            />
            <Toggle
              label="Let learners mark lessons complete"
              value={draft.lms_manual_completion !== false}
              disabled={!canWrite}
              description="Shows the Mark complete button in the lesson player. Turn it off and a video lesson completes only once it has been watched (90%), and an article once the learner scrolls to its end. An embedded video the player cannot track keeps the button, since nothing else could complete it. Lessons already completed stay completed."
              onChange={(v) => setDraft({ ...draft, lms_manual_completion: v })}
            />
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="lms-nudge-days">
                Nudge inactive learners after (days)
              </label>
              <Input
                id="lms-nudge-days"
                type="number"
                min={0}
                max={365}
                step={1}
                className="w-40"
                value={draft.lms_nudge_after_days ?? ''}
                disabled={!canWrite}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    lms_nudge_after_days:
                      e.target.value === ''
                        ? null
                        : Math.min(365, Math.max(0, Math.floor(Number(e.target.value) || 0))),
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                Sends ONE in-app reminder to a learner who has an unfinished course and hasn't
                opened any lesson for this many days, naming the course they touched most recently.
                A learner active in any course is never nudged, and finished, revoked or expired
                courses don't count. One reminder per learner per period; staff accounts are
                skipped. Blank or 0 turns reminders off. The wording lives under Notifications →
                Learning notifications → "Learning reminder".
              </p>
            </div>
          </CardContent>
        </Card>

        <Card id="creators" className="scroll-mt-20">
          <CardHeader>
            <CardTitle>Creators</CardTitle>
            <p className="text-sm text-muted-foreground">
              The platform's cut of a third-party creator's sales, and what an applicant must
              provide.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="creator-fee">
                Creator platform fee — default (%)
              </label>
              <Input
                id="creator-fee"
                type="number"
                min={0}
                max={100}
                value={draft.creator_platform_fee_pct ?? ''}
                disabled={!canWrite}
                onChange={(e) => setDraft({ ...draft, creator_platform_fee_pct: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Charged on every Content Creator course sale (per-creator override at approval).
                Snapshotted onto each order line at checkout — changing it never rewrites a past
                sale. The creator earns price × (1 − fee%); the platform keeps the fee.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="creator-fee-min">
                  Min creator fee (%)
                </label>
                <Input
                  id="creator-fee-min"
                  type="number"
                  min={0}
                  max={100}
                  value={draft.creator_platform_fee_min_pct ?? ''}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setDraft({ ...draft, creator_platform_fee_min_pct: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="creator-fee-max">
                  Max creator fee (%)
                </label>
                <Input
                  id="creator-fee-max"
                  type="number"
                  min={0}
                  max={100}
                  value={draft.creator_platform_fee_max_pct ?? ''}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setDraft({ ...draft, creator_platform_fee_max_pct: e.target.value })
                  }
                />
              </div>
            </div>
            <Toggle
              label="Creator course sales earn MLM commissions"
              description="When OFF (default) creator course sales are marketplace-only: no PV, no comp — they only credit the creator wallet. When ON, a completed creator order also fires the comp event like a house sale. House (admin-authored) courses always behave like normal virtual products."
              value={draft.creator_sales_earn_comp ?? false}
              disabled={!canWrite}
              onChange={(v) => setDraft({ ...draft, creator_sales_earn_comp: v })}
            />
            <MerchantRequirementsEditor
              value={draft.creator_requirements ?? []}
              disabled={!canWrite}
              applicant="creator"
              onChange={(reqs) => setDraft({ ...draft, creator_requirements: reqs })}
            />
          </CardContent>
        </Card>

        <Card id="media" className="scroll-mt-20">
          <CardHeader>
            <CardTitle>Video and storage</CardTitle>
            <p className="text-sm text-muted-foreground">
              Upload limits, which video hosts a lesson may embed, and how long a replaced file is
              kept before the bucket sweep removes it.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="course-video-max">
                Lesson video upload cap (MB)
              </label>
              <Input
                id="course-video-max"
                type="number"
                min={1}
                max={5000}
                value={draft.course_video_max_mb ?? 1024}
                disabled={!canWrite}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    course_video_max_mb: Number(e.target.value) || 1024,
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                Applies to MP4 lessons uploaded to storage (YouTube/Vimeo links are unlimited). The
                browser uploads straight to storage — the API never buffers video.
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="course-embed-hosts">
                Allowed video hosts for lessons
              </label>
              <Textarea
                id="course-embed-hosts"
                rows={3}
                disabled={!canWrite}
                value={(draft.course_embed_hosts ?? []).join('\n')}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    course_embed_hosts: e.target.value
                      .split(/[\n,]/)
                      .map((h) =>
                        h
                          .trim()
                          .toLowerCase()
                          .replace(/^https?:\/\//, ''),
                      )
                      .filter(Boolean),
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                One host per line. A lesson video link must be on this list, or the lesson cannot be
                saved. Links already saved on a host you remove stop playing. Direct video files
                (.mp4 and similar) over https always work. Leave the defaults unless a course needs
                another provider.
              </p>
            </div>
            <Toggle
              label="Reclaim unused storage"
              description="Deletes course videos, attachments and captions that nothing points at any more — a replaced video, a deleted lesson, an archived member's files. Nothing is deleted immediately: an orphan waits out the grace period below, and its references are re-checked before the bytes go. Turning this OFF pauses deletion but keeps tracking orphans, so switching it back on still cleans up everything found while it was off."
              value={draft.storage_sweep_enabled ?? true}
              disabled={!canWrite}
              onChange={(v) => setDraft({ ...draft, storage_sweep_enabled: v })}
            />
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="storage-grace-days">
                Storage grace period (days)
              </label>
              <Input
                id="storage-grace-days"
                type="number"
                min={0}
                max={90}
                value={draft.storage_delete_grace_days ?? 7}
                disabled={!canWrite}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    storage_delete_grace_days: Number(e.target.value) || 0,
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                How long an unused file is kept before it is deleted. This is the window in which an
                undo, a rolled-back deploy or a restored backup can still put the file back. 0
                deletes on the next sweep.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card id="storefront" className="scroll-mt-20">
          <CardHeader>
            <CardTitle>Course pages</CardTitle>
            <p className="text-sm text-muted-foreground">
              What the public course pages show. Their words and pictures are edited under{' '}
              <Link to="/learning-pages" className="underline">
                Learning → Learning Pages
              </Link>
              .
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <Toggle
              label="Visitors can browse courses"
              description="People who are not signed in can see the course list, each course's page and the instructors, on the website and in the coach app. Off, they are asked to log in first; this is enforced by the server, and course products also leave the public shop list. Default on."
              value={draft.lms_public_catalog !== false}
              disabled={!canWrite}
              onChange={(v) => setDraft({ ...draft, lms_public_catalog: v })}
            />
            <Toggle
              label="Phone app: send people to the website for courses"
              description="In the Android coach app, a course someone cannot open yet gets a button that opens the same course on the website, where it can be bought: Join Miracles Protocol for a visitor, Continue on the website for a member. Google Play's rules forbid pointing app users to another way of paying for digital content, so turning this on is accepting a risk: Google may reject an app update or ask for the link to be removed. Turn it off again at any time; the app follows on its next load. Off by default."
              value={draft.lms_app_web_link === true}
              disabled={!canWrite}
              onChange={(v) => setDraft({ ...draft, lms_app_web_link: v })}
            />
            <Toggle
              label="Show course ratings and student counts"
              description="Real, earned numbers: star ratings, the reviews section and how many members are enrolled. Off hides all three everywhere — it does NOT affect the decorative numbers on marketplace product cards, which have their own setting."
              value={storefront.showCourseRatings}
              disabled={!canWrite}
              onChange={(v) => setStorefront({ ...storefront, showCourseRatings: v })}
            />
            <Toggle
              label="Show the Share & earn surfaces"
              description="Gates all three at once: the header link, the band at the bottom of the course pages, and the resell value prop. Off for a tenant that sells courses without a referral programme."
              value={storefront.showResellBanner}
              disabled={!canWrite}
              onChange={(v) => setStorefront({ ...storefront, showResellBanner: v })}
            />
            <Toggle
              label="Link from the course pages to the store"
              description={'Adds a "Store" link to the course header.'}
              value={storefront.learningHome.linkToStore}
              disabled={!canWrite}
              onChange={(v) =>
                setStorefront({
                  ...storefront,
                  learningHome: { ...storefront.learningHome, linkToStore: v },
                })
              }
            />
            <Toggle
              label="Link from the store to the course pages"
              description={'Adds a "Courses" link to the marketplace header.'}
              value={storefront.learningHome.linkFromStore}
              disabled={!canWrite}
              onChange={(v) =>
                setStorefront({
                  ...storefront,
                  learningHome: { ...storefront.learningHome, linkFromStore: v },
                })
              }
            />
          </CardContent>
        </Card>

        <Card id="notifications" className="scroll-mt-20">
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
            <p className="text-sm text-muted-foreground">
              What the LMS emails and in-app bells say. Placeholders:{' '}
              <code>{'{course_title}'}</code>, <code>{'{lesson_title}'}</code>,{' '}
              <code>{'{test_title}'}</code>, <code>{'{score}'}</code>, <code>{'{pass_mark}'}</code>,{' '}
              <code>{'{learner_name}'}</code>, <code>{'{creator_name}'}</code>,{' '}
              <code>{'{amount}'}</code>, <code>{'{reason}'}</code>. Every other event lives under
              Settings → System → Notifications.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="mb-2 text-sm font-semibold">Learners</h3>
              <div className="space-y-2">
                {LMS_LEARNER_NOTIFICATION_EVENT_CODES.map((code) => (
                  <EventTemplateEditor
                    key={code}
                    code={code}
                    draft={draft}
                    setDraft={setDraft}
                    canWrite={canWrite}
                  />
                ))}
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold">Creators</h3>
              <div className="space-y-2">
                {LMS_CREATOR_NOTIFICATION_EVENT_CODES.map((code) => (
                  <EventTemplateEditor
                    key={code}
                    code={code}
                    draft={draft}
                    setDraft={setDraft}
                    canWrite={canWrite}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <SaveButton />
        </div>
      </div>
    </div>
  );
}
